import { z } from 'zod';
import { basisSchema, calcMethodSchema, inferenceSourceSchema, confidenceSchema } from './charge';

export const parsedLineItemSchema = z.object({
  // LLM-assigned stable ID (e.g. "L1"). Optional for backward compat with
  // fixtures or callers that pre-date the structured-ref refactor.
  id: z.string().optional(),
  name: z.string(),
  description: z.string().nullable().default(null),
  quantity: z.number().int().nullable().default(null),
  unitCost: z.number().int(),
  section: z.string().nullable().default(null),
});

export const basisRefSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('subtotal') }),
  z.object({ kind: z.literal('lineItems'), ids: z.array(z.string()).min(1) }),
  z.object({ kind: z.literal('charges'), ids: z.array(z.string()).min(1) }),
  z.object({
    kind: z.literal('composite'),
    lineItemIds: z.array(z.string()).optional(),
    chargeIds: z.array(z.string()).optional(),
    includeSubtotal: z.boolean().optional(),
  }),
]);

export const parsedChargeSchema = z.object({
  // LLM-assigned stable ID (e.g. "C1"). Optional for backward compat.
  id: z.string().optional(),
  type: z.enum(['delivery', 'labor', 'tax', 'other']),
  description: z.string().nullable().default(null),
  amount: z.number().int(),
  // LLM-emitted hints
  calcMethod: calcMethodSchema,
  ratePercent: z.number().nonnegative().optional(),
  // Structured reference to what the percentage applies to.
  basisRef: basisRefSchema.optional(),
  // Server-resolved formula fields (set by /parse-document so the
  // frontend can pass them straight through to /api/quotes on save).
  rateBps: z.number().int().nullable().optional(),
  basis: basisSchema.nullable().optional(),
  inferenceSource: inferenceSourceSchema.optional(),
  confidence: confidenceSchema.nullable().optional(),
});

export const parsedAddOnSchema = z.object({
  name: z.string(),
  description: z.string().nullable().default(null),
  quantity: z.number().int().nullable().default(null),
  unitCost: z.number().int(),
  section: z.string().nullable().default(null),
});

export const parsedPackageSchema = z.object({
  name: z.string(),
  description: z.string().nullable().default(null),
  lineItems: z.array(parsedLineItemSchema),
  additionalCharges: z.array(parsedChargeSchema).default([]),
  statedTotal: z.number().int().nullable().default(null),
});

export const vendorContactInfoSchema = z.object({
  contactName: z.string().nullable().default(null),
  email: z.string().nullable().default(null),
  phone: z.string().nullable().default(null),
  website: z.string().nullable().default(null),
});

export const quoteImportResultSchema = z.object({
  vendorName: z.string(),
  packages: z.array(parsedPackageSchema),
  addOns: z.array(parsedAddOnSchema).default([]),
  additionalCharges: z.array(parsedChargeSchema).default([]),
  confidence: z.enum(['high', 'medium', 'low']),
  warnings: z.array(z.string()).default([]),
  vendorContactInfo: vendorContactInfoSchema.default({ contactName: null, email: null, phone: null, website: null }),
  suggestedCategory: z.string().default('Uncategorized'),
});

export type QuoteImportResultOutput = z.infer<typeof quoteImportResultSchema>;
